import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { 
  CheckCircle, 
  ArrowRight, 
  Shield, 
  Zap, 
  FileCheck, 
  Calculator,
  Clock,
  Target,
  TrendingUp,
  Eye,
  Database,
  Brain,
  Cpu,
  Building,
  Users,
  Award,
  AlertTriangle,
  BarChart3
} from "lucide-react";

const CPAPage = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleScheduleCall = (e) => {
    e.preventDefault();
    window.open('https://calendly.com/advisorxai/30min', '_blank');
  };

  const handleGetStarted = (e) => {
    e.preventDefault();
    window.open('https://calendly.com/advisorxai/30min', '_blank');
  };

  return (
    <div className="min-h-screen">
      {/* Header Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#112b3c]/95 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <img 
                src="/credentra-logo-new.png" 
                alt="Credentra Logo" 
                className="h-52 w-auto"
              />
            </div>
            <div className="nav-links">
              <a href="/" className="nav-link">Home</a>
              <a href="/#features" className="nav-link">Features</a>
              <a href="/#founder" className="nav-link">Founder</a>
              <a href="/#blog" className="nav-link">Blog</a>
              <Button 
                size="sm" 
                onClick={handleScheduleCall}
                className="text-white bg-credentra-bright-teal hover:bg-credentra-bright-teal/90 font-medium px-4 py-2 ml-4"
              >
                Schedule Demo
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - CPA Focused */}
      <section className="text-white py-20 relative overflow-hidden" style={{background: '#112b3c', paddingTop: '120px'}}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          
          {/* Hero Badge */}
          <div className="text-center pt-2 md:pt-1 pb-8 sm:pb-12">
            <div className="hero-badge inline-flex items-center gap-2 mb-8 sm:mb-12" style={{background: '#112b3c'}}>
              <Users className="h-4 w-4" />
              <span className="leading-tight">
                Professional Tax Credit Compliance Platform for CPAs
              </span>
            </div>
          </div>
          
          {/* Hero Content */}
          <div className="text-center hero-content">
            <h1 className={`hero-title ${isVisible ? 'animate-slide-up' : ''}`} style={{animationDelay: '0.1s'}}>
              <span>Your AI Copilot for
              <span className="block">FICA Tip Credit Compliance</span></span>
            </h1>
            
            <p className={`hero-subtitle ${isVisible ? 'animate-slide-up' : ''}`} style={{animationDelay: '0.2s'}}>
              Deliver $10,000-$100,000+ in tax savings to your hospitality clients in days, not months. Eliminate tedious data work while generating IRS-audit-ready documentation with AI precision.
            </p>
            
            <div className={`flex flex-col sm:flex-row gap-4 justify-center items-center mt-10 ${isVisible ? 'animate-slide-up' : ''}`} style={{animationDelay: '0.3s'}}>
              <Button 
                size="lg"
                className="text-white font-semibold text-lg px-10 py-6 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
                style={{background: 'linear-gradient(135deg, #00d6cf, #3c778d)', boxShadow: '0 4px 20px rgba(0, 214, 207, 0.3)'}}
                onClick={handleGetStarted}
              >
                <Cpu className="mr-2 h-5 w-5" />
                Get Started
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                className="text-credentra-bright-teal border-credentra-bright-teal hover:bg-credentra-bright-teal hover:text-white font-semibold text-lg px-8 py-6 rounded-lg transition-all duration-300"
                onClick={handleScheduleCall}
              >
                Schedule Demo
              </Button>
            </div>
            
            {/* Trust Indicators for CPAs */}
            <div className="flex flex-wrap justify-center items-center gap-6 mt-16 text-white/60 text-sm">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                <span className="text-white/90 font-medium">IRS Audit Ready</span>
              </div>
              <div className="flex items-center gap-2">
                <FileCheck className="h-4 w-4" />
                <span className="text-white/90 font-medium">Complete Audit Trail</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="h-4 w-4" />
                <span className="text-white/90 font-medium">Professional Grade</span>
              </div>
            </div>
          </div>
        </div>

        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-credentra-bright-teal/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-credentra-bright-teal/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>
      </section>

      {/* CPA Pain Points & Solutions */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="section-badge text-white" style={{background: '#112b3c'}}>
              <AlertTriangle className="h-3 w-3" />
              CPA Challenges
            </div>
            <h2 className="enterprise-heading text-4xl md:text-5xl font-bold text-credentra-dark-teal mb-6">
              Stop Wrestling with FICA Tip Credit Data
            </h2>
            <p className="enterprise-subheading text-xl max-w-3xl mx-auto">
              Transform hours of manual work into minutes of AI-powered precision
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Pain Points */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-credentra-dark-teal mb-6">Current Reality</h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 bg-red-50 rounded-lg border-l-4 border-red-400">
                  <Clock className="h-5 w-5 text-red-500 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Hours of Manual Data Work</h4>
                    <p className="text-gray-600 text-sm">Consolidating POS systems, payroll records, and tip reporting across multiple locations</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 bg-orange-50 rounded-lg border-l-4 border-orange-400">
                  <AlertTriangle className="h-5 w-5 text-orange-500 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Audit Trail Anxiety</h4>
                    <p className="text-gray-600 text-sm">Manually creating documentation that satisfies IRS audit requirements</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 bg-yellow-50 rounded-lg border-l-4 border-yellow-400">
                  <Database className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Data Validation Headaches</h4>
                    <p className="text-gray-600 text-sm">Cross-referencing tip reports against payroll taxes and wage calculations</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Solutions */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-credentra-dark-teal mb-6">With Credentra</h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg border-l-4 border-green-400">
                  <Zap className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Automated Data Integration</h4>
                    <p className="text-gray-600 text-sm">AI connects and consolidates all data sources in minutes, not hours</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                  <Shield className="h-5 w-5 text-blue-500 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-gray-900">IRS-Ready Documentation</h4>
                    <p className="text-gray-600 text-sm">Complete audit trails generated automatically with professional formatting</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 bg-purple-50 rounded-lg border-l-4 border-purple-400">
                  <CheckCircle className="h-5 w-5 text-purple-500 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Validated Calculations</h4>
                    <p className="text-gray-600 text-sm">AI cross-checks all data points and flags discrepancies before filing</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CPA Workflow Integration */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="section-badge text-white" style={{background: '#112b3c'}}>
              <Cpu className="h-3 w-3" />
              Workflow
            </div>
            <h2 className="enterprise-heading text-4xl md:text-5xl font-bold text-credentra-dark-teal mb-6">
              Seamlessly Integrates with Your Practice
            </h2>
            <p className="enterprise-subheading text-xl max-w-3xl mx-auto">
              Designed by CPAs, for CPAs. Fits naturally into your existing client service workflow.
            </p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="text-center group">
              <div className="enterprise-card text-center p-8">
                <div className="enterprise-icon mx-auto mb-6">
                  <Database className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-credentra-dark-teal mb-3">1. Client Data Import</h3>
                <p className="enterprise-subheading">Your clients grant secure access to their POS and payroll systems. Credentra handles the technical integration.</p>
              </div>
            </div>

            <div className="text-center group">
              <div className="enterprise-card text-center p-8">
                <div className="enterprise-icon mx-auto mb-6">
                  <Cpu className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-credentra-dark-teal mb-3">2. AI Processing</h3>
                <p className="enterprise-subheading">Our AI validates data, calculates credits, and generates complete documentation packages for your review.</p>
              </div>
            </div>

            <div className="text-center group">
              <div className="enterprise-card text-center p-8">
                <div className="enterprise-icon mx-auto mb-6">
                  <FileCheck className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-credentra-dark-teal mb-3">3. Professional Delivery</h3>
                <p className="enterprise-subheading">You receive polished, audit-ready forms and supporting documentation to file with confidence.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Model Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="section-badge text-white" style={{background: '#112b3c'}}>
              <TrendingUp className="h-3 w-3" />
              Partnership Model
            </div>
            <h2 className="enterprise-heading text-4xl md:text-5xl font-bold text-credentra-dark-teal mb-6">
              Profitable Partnership for Your Firm
            </h2>
            <p className="enterprise-subheading text-xl max-w-3xl mx-auto">
              Transform FICA tip credit compliance into a high-value, recurring revenue stream
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-credentra-dark-teal mb-6">Revenue Opportunity</h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 bg-green-50 rounded-lg border-l-4 border-green-400">
                  <BarChart3 className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Success Fee Sharing</h4>
                    <p className="text-gray-600 text-sm">Earn competitive fees on every successful credit recovery - shared on credits actually claimed</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                  <TrendingUp className="h-5 w-5 text-blue-500 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Recurring Opportunity</h4>
                    <p className="text-gray-600 text-sm">Annual compliance service for existing clients, plus new client acquisition</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 bg-purple-50 rounded-lg border-l-4 border-purple-400">
                  <Award className="h-5 w-5 text-purple-500 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Premium Positioning</h4>
                    <p className="text-gray-600 text-sm">Offer cutting-edge AI tax services that differentiate your practice</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-credentra-dark-teal mb-6">Client Value Delivery</h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 bg-teal-50 rounded-lg border-l-4 border-teal-400">
                  <Clock className="h-5 w-5 text-teal-500 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Rapid Results</h4>
                    <p className="text-gray-600 text-sm">Deliver tax savings in days instead of months of manual work</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 bg-orange-50 rounded-lg border-l-4 border-orange-400">
                  <Target className="h-5 w-5 text-orange-500 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Significant Impact</h4>
                    <p className="text-gray-600 text-sm">$10,000-$100,000+ annual savings create lasting client relationships</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-4 bg-indigo-50 rounded-lg border-l-4 border-indigo-400">
                  <Eye className="h-5 w-5 text-indigo-500 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Professional Excellence</h4>
                    <p className="text-gray-600 text-sm">AI-powered accuracy and audit-ready documentation enhance your reputation</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 text-white relative overflow-hidden" style={{background: '#112b3c'}}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Turn Tax Compliance Into Competitive Advantage
          </h2>
          <p className="text-xl mb-10 text-white/90">
            While other firms struggle with manual FICA tip credit work, you'll be delivering six-figure tax savings to clients in days. Let's discuss how this partnership transforms your hospitality practice.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              size="lg"
              className="text-white font-semibold text-lg px-10 py-6 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
              style={{background: 'linear-gradient(135deg, #00d6cf, #3c778d)', boxShadow: '0 4px 20px rgba(0, 214, 207, 0.3)'}}
              onClick={handleScheduleCall}
            >
              <Users className="mr-2 h-5 w-5" />
              Schedule Partnership Discussion
            </Button>
          </div>
        </div>

        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-credentra-bright-teal/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-credentra-bright-teal/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>
      </section>
    </div>
  );
};

export default CPAPage;